import React, { useState, useMemo } from "react";
import {
  Box,
  Text,
  Layer,
  Button,
  TextInput,
  Tabs,
  Tab,
  Card,
  CardHeader,
  CardBody,
  Heading,
} from "grommet";

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function parseExamCodeData(examCode) {
  if (!hasValue(examCode)) return null;

  if (typeof examCode === "object") {
    return examCode;
  }

  if (typeof examCode === "string") {
    try {
      return JSON.parse(examCode);
    } catch (_) {
      return null;
    }
  }

  return null;
}

function getExamCodeFields(examCode) {
  const parsed = parseExamCodeData(examCode);
  if (!parsed || typeof parsed !== "object") return null;

  if (Array.isArray(parsed.fields)) {
    return parsed.fields
      .filter((field) => field && hasValue(field.key))
      .map((field) => ({
        key: field.key,
        value: field.value ?? "",
      }));
  }

  return Object.entries(parsed)
    .filter(
      ([key, value]) =>
        hasValue(key) && (value === null || typeof value !== "object"),
    )
    .map(([key, value]) => ({ key, value: value ?? "" }));
}

function getExamCodeSearchText(examCode) {
  const fields = getExamCodeFields(examCode);
  if (fields?.length) {
    return fields.map(({ key, value }) => `${key} ${value}`).join(" ");
  }

  return typeof examCode === "string" ? examCode : "";
}

export default function PodExamCodesLayer({
  podExamCodes = [],
  podSession,
  setShowExamCodesLayer,
  showExamCodesLayer,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  // Function to get display name from exam code item
  const getDisplayName = (item) => {
    if (hasValue(item.full_name)) {
      return item.full_name;
    } else if (hasValue(item.first_name) && hasValue(item.last_name)) {
      return `${item.first_name} ${item.last_name}`;
    } else if (hasValue(item.first_name)) {
      return item.first_name;
    } else if (hasValue(item.last_name)) {
      return item.last_name;
    }
    return "No name available";
  };

  // Function to get display email
  const getDisplayEmail = (item) => {
    return hasValue(item.email) ? item.email : "No email available";
  };

  // Function to get display candidate ID
  const getDisplayCandidateId = (item) => {
    return hasValue(item.candidate_id) ? item.candidate_id : "No candidate ID";
  };

  // Function to normalize strings for comparison
  const normalizeString = (str) => {
    if (!str) return "";
    return str.toLowerCase().trim();
  };

  // Find matched exam codes based on podSession (email and candidate_id only)
  const matchedExamCodes = useMemo(() => {
    if (!podSession || !podExamCodes.length) return [];

    const testerEmail = normalizeString(podSession.testerEmail);
    const testerCandidateId = normalizeString(podSession.testerCandidateId);

    const matches = podExamCodes.filter((item) => {
      const email = normalizeString(item.email);
      const candidateId = normalizeString(item.candidate_id);

      // Check for email matches
      const emailMatches =
        testerEmail &&
        email &&
        (email.includes(testerEmail) || testerEmail.includes(email));

      // Check for candidate_id matches (uses testerCandidateId when present, else testerEmail)
      const candidateIdValue = testerCandidateId || testerEmail;
      const candidateIdMatches =
        candidateIdValue &&
        candidateId &&
        (candidateId.includes(candidateIdValue) ||
          candidateIdValue.includes(candidateId));

      // Return true only if we have at least one valid match (email or candidate_id)
      return emailMatches || candidateIdMatches;
    });

    // Remove duplicates based on id
    const uniqueMatches = matches.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id)
    );

    return uniqueMatches;
  }, [podExamCodes, podSession]);

  // Filter exam codes based on search term
  const filteredExamCodes = useMemo(() => {
    if (!hasSearched) return [];
    if (!searchTerm.trim()) return podExamCodes;

    const searchLower = searchTerm.toLowerCase();
    return podExamCodes.filter((item) => {
      const searchableFields = [
        item.full_name,
        item.first_name,
        item.last_name,
        item.candidate_id,
        item.email,
        item.exam_name,
        getExamCodeSearchText(item.exam_code),
      ].filter(hasValue);

      return searchableFields.some((field) =>
        field.toLowerCase().includes(searchLower)
      );
    });
  }, [hasSearched, podExamCodes, searchTerm]);

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setHasSearched(true);
  };

  // Render exam code item
  const renderExamCodeItem = (item, index) => (
    <Card key={item.id || index} margin={{ bottom: "small" }} pad="small">
      <CardHeader>
        <Heading level="4" margin="none">
          {getDisplayName(item)}
        </Heading>
      </CardHeader>
      <CardBody>
        <Box gap="xsmall">
          <Text size="small">
            <strong>Email:</strong> {getDisplayEmail(item)}
          </Text>
          <Text size="small">
            <strong>Candidate ID:</strong> {getDisplayCandidateId(item)}
          </Text>
          {hasValue(item.exam_name) && (
            <Text size="small">
              <strong>Exam Name:</strong> {item.exam_name}
            </Text>
          )}
          {hasValue(item.exam_code) && (() => {
            const fields = getExamCodeFields(item.exam_code);

            if (fields?.length) {
              return fields.map(({ key, value }, fieldIndex) => (
                <Text key={`${key}-${fieldIndex}`} size="small">
                  <strong>{key}:</strong> {String(value)}
                </Text>
              ));
            }

            return (
              <Text size="small">
                <strong>Exam Code:</strong>{" "}
                {typeof item.exam_code === "string"
                  ? item.exam_code
                  : JSON.stringify(item.exam_code)}
              </Text>
            );
          })()}
        </Box>
      </CardBody>
    </Card>
  );

  return (
    <Layer
      position="right"
      full="vertical"
      modal
      onClickOutside={() => setShowExamCodesLayer(false)}
    >
      <Box pad="medium" fill overflow="auto" width="xlarge">
        <Box
          direction="row"
          justify="between"
          align="center"
          margin={{ bottom: "medium" }}
        >
          <Heading level="3" margin="none">
            Exam Codes
          </Heading>
          <Button label="Close" onClick={() => setShowExamCodesLayer(false)} />
        </Box>

        <Tabs activeIndex={activeTab} onActive={setActiveTab}>
          <Tab title="Matched">
            <Box pad={{ top: "medium" }} overflow="auto" width="large">
              <Text margin={{ bottom: "medium" }}>
                Found {matchedExamCodes.length} matching exam code(s) based on
                tester information:
              </Text>
              {podSession && (
                <Box
                  margin={{ bottom: "medium" }}
                  pad="small"
                  background="light-2"
                  round="small"
                >
                  <Text size="small">
                    <strong>Tester Name:</strong>{" "}
                    {podSession.testerName || "Not provided"}
                  </Text>
                  <Text size="small">
                    <strong>Tester Email:</strong>{" "}
                    {podSession.testerEmail || "Not provided"}
                  </Text>
                </Box>
              )}
              {matchedExamCodes.length > 0 ? (
                <Box>
                  {matchedExamCodes.map((item, index) =>
                    renderExamCodeItem(item, index)
                  )}
                </Box>
              ) : (
                <Text>No matching exam codes found.</Text>
              )}
            </Box>
          </Tab>

          <Tab title="All Exam Codes">
            <Box pad={{ top: "medium" }} overflow="auto" width="large">
              <Box margin={{ bottom: "medium" }}>
                <Box direction="row" gap="small" align="center">
                  <TextInput
                    placeholder="Search exam codes..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  <Button label="Search" onClick={handleSearch} primary />
                </Box>
                <Text size="small">
                  Search by name, email, candidate ID, or exam name
                </Text>
              </Box>
              {hasSearched ? (
                <Text margin={{ bottom: "medium" }}>
                  Showing {filteredExamCodes.length} of {podExamCodes.length} exam
                  code(s)
                </Text>
              ) : (
                <Text margin={{ bottom: "medium" }}>
                  Enter a search term and click Search to view exam code(s).
                </Text>
              )}
              {hasSearched && filteredExamCodes.length > 0 ? (
                <Box>
                  {filteredExamCodes.map((item, index) =>
                    renderExamCodeItem(item, index)
                  )}
                </Box>
              ) : hasSearched ? (
                <Text>No exam codes found.</Text>
              ) : (
                <Text>Search has not been executed yet.</Text>
              )}
            </Box>
          </Tab>
        </Tabs>
      </Box>
    </Layer>
  );
}
